import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Car, Calendar, Bell, User, Settings, LogOut, Plus, 
  DollarSign, TrendingUp, Clock, CheckCircle, AlertCircle,
  FileText, Menu, X, MessageCircle, Headphones, AlertTriangle, Shield,
  Wallet, CreditCard, AlertOctagon, BarChart3, ChevronRight, ShieldCheck,
  ClipboardCheck, ChevronDown, Loader2, ArrowRight, Zap, Lock, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { getVehiclePhotoUrl, filterValidPhotos, handleVehicleImageError } from '../lib/imageUtils';
import HostInviteAndEarn from '../components/HostInviteAndEarn';
import { OnboardingChecklist } from '../components/OnboardingChecklist';
import { HostAppShell } from '../components/HostAppShell';
import PaymentCountdown from '../components/PaymentCountdown';
import HostTrackerAlertsPanel from '../components/HostTrackerAlertsPanel';
import HostOnboardingHelperModal, { HostOnboardingHelperLink } from '../components/HostOnboardingHelperModal';
import { getHostDashboardBadge, normalizeHostStatus, getHostStatusConfig } from '../lib/hostBookingStatus';

// ============================================================================
// SIMPLE EARNINGS CTA CARD (Replaces detailed earnings timeline)
// Links to /host/payouts which is now the Financial Hub
// ============================================================================

const EarningsCTACard = () => {
  return (
    <Link to="/host/payouts" data-testid="earnings-cta-card">
      <Card className="border-slate-100 mt-4 hover:shadow-md hover:-translate-y-0.5 active:scale-[0.99] transition-all duration-150 cursor-pointer group">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Earnings & Payouts</h3>
                <p className="text-sm text-slate-500">View your earnings, payouts, and financial history</p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-green-600 group-hover:translate-x-1 transition-all" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

// ============================================================================

// ============================================================================
// HOST TIER OVERVIEW CARD — Shows current plan and feature access at a glance
// ============================================================================
const TIER_CONFIG = {
  free: { label: 'Free', color: 'from-slate-50 to-slate-100', badgeColor: 'bg-slate-100 text-slate-600 border-slate-200', accent: 'text-slate-600' },
  basic: { label: 'Basic', color: 'from-blue-50 to-blue-100', badgeColor: 'bg-blue-100 text-blue-700 border-blue-200', accent: 'text-blue-700' },
  pro: { label: 'Pro', color: 'from-violet-50 to-violet-100', badgeColor: 'bg-violet-100 text-violet-700 border-violet-200', accent: 'text-violet-700' },
  premium: { label: 'Premium', color: 'from-amber-50 to-amber-100', badgeColor: 'bg-amber-100 text-amber-700 border-amber-200', accent: 'text-amber-700' },
};

const FEATURE_LIST = [
  { key: 'boost_listing', label: 'Boost Listing', tier: 'basic' },
  { key: 'priority_support', label: 'Priority Support', tier: 'basic' },
  { key: 'advanced_analytics', label: 'Advanced Analytics', tier: 'pro' },
  { key: 'gps_tracking', label: 'GPS Tracking', tier: 'pro' },
  { key: 'ai_assistant', label: 'AI Assistant', tier: 'premium' },
  { key: 'insurance_perks', label: 'Insurance Perks', tier: 'premium' },
];

const HostTierCard = ({ tier, hasFeature }) => {
  const config = TIER_CONFIG[tier] || TIER_CONFIG.free;
  const unlockedCount = FEATURE_LIST.filter(f => hasFeature(f.key)).length;
  
  return (
    <Link to="/host/plan" data-testid="host-tier-card">
      <Card className={`border-0 bg-gradient-to-r ${config.color} shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-150 cursor-pointer group`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Zap className={`w-4 h-4 ${config.accent}`} />
              <span className="text-sm font-semibold text-slate-800">Your Plan</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-bold border ${config.badgeColor}`}>
                {config.label}
              </span>
              <ChevronRight className="w-4 h-4 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </div>
        <div className="flex flex-wrap gap-1.5">
          {FEATURE_LIST.map(f => {
            const unlocked = hasFeature(f.key);
            return (
              <span
                key={f.key}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                  unlocked
                    ? 'bg-white/80 text-slate-700 border border-slate-200'
                    : 'bg-white/40 text-slate-400 border border-dashed border-slate-200'
                }`}
                data-testid={`tier-feature-${f.key}`}
              >
                {unlocked ? <CheckCircle className="w-3 h-3 text-green-500" /> : <Lock className="w-3 h-3" />}
                {f.label}
              </span>
            );
          })}
        </div>
        {unlockedCount < FEATURE_LIST.length && (
          <p className="text-xs text-slate-500 mt-2">
            {unlockedCount} of {FEATURE_LIST.length} tools unlocked
          </p>
        )}
      </CardContent>
    </Card>
    </Link>
  );
};

// ============================================================================

const HostDashboard = () => {
  const { user, logout, api, isSuperAdmin, isAdmin, isPlatformOwner, isHost, refreshUser, updateUser, hasFeature, getSubscriptionTier } = useAuth();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [becomingHost, setBecomingHost] = useState(false);
  
  // New: Dashboard summary with financial data
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [nextPayout, setNextPayout] = useState(null);
  
  // Protection status from action flags
  const [protectionStatus, setProtectionStatus] = useState({ 
    status: 'loading', 
    issues: [],
    checklistsMissing: 0,
    complianceIssues: 0
  });

  useEffect(() => {
    // P0 CRITICAL: Platform owner should be redirected to admin
    if (isPlatformOwner()) {
      navigate('/admin');
      return;
    }
    
    // P0 CRITICAL: Only fetch host data if user is actually a host
    if (isHost()) {
      fetchData();
      fetchDashboardSummary();
      fetchNextPayout();
      fetchProtectionStatus();
    } else {
      setLoading(false);
      setSummaryLoading(false);
    }
  }, [user?.host_status, isPlatformOwner]);

  const fetchData = async () => {
    try {
      const [vehiclesRes, notificationsRes] = await Promise.all([
        api.get('/vehicles/my-vehicles'),
        api.get('/notifications?unread_only=true')
      ]);
      setVehicles(vehiclesRes.data.vehicles || []);
      setNotifications(notificationsRes.data.notifications || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardSummary = async () => {
    try {
      const res = await api.get('/host/dashboard-summary');
      setDashboardSummary(res.data);
      // Use bookings from summary instead of separate call
      setBookings(res.data.recent_bookings || []);
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
      // Fallback: fetch bookings separately
      try {
        const bookingsRes = await api.get('/bookings?role=owner');
        setBookings(bookingsRes.data.bookings || []);
      } catch (e) {
        console.error('Error fetching bookings fallback:', e);
      }
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchNextPayout = async () => {
    try {
      const res = await api.get('/host/payouts/next');
      setNextPayout(res.data);
    } catch (error) {
      console.error('Error fetching next payout:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  // Fetch protection status from action flags
  const fetchProtectionStatus = async () => {
    try {
      const response = await api.get('/host/action-flags');
      const flags = response.data.flags || [];
      
      // Analyze flags for protection-relevant issues
      const checklistFlags = flags.filter(f => 
        f.type?.includes('prehire') || f.type?.includes('posthire')
      );
      const complianceFlags = flags.filter(f => 
        f.type?.includes('insurance') || f.type?.includes('licence') || 
        f.type?.includes('rego') || f.type?.includes('compliance')
      );
      
      const hasChecklistIssues = checklistFlags.length > 0;
      const hasComplianceIssues = complianceFlags.length > 0;
      
      let status = 'protected';
      if (hasChecklistIssues && hasComplianceIssues) {
        status = 'issues';
      } else if (hasChecklistIssues) {
        status = 'checklist_missing';
      } else if (hasComplianceIssues) {
        status = 'compliance_issue';
      }
      
      setProtectionStatus({
        status,
        issues: flags,
        checklistsMissing: checklistFlags.length,
        complianceIssues: complianceFlags.length
      });
    } catch (error) {
      console.error('Error fetching protection status:', error);
      setProtectionStatus({ status: 'protected', issues: [], checklistsMissing: 0, complianceIssues: 0 });
    }
  };

  // P0 HOTFIX: Handle Become Host action
  const handleBecomeHost = async () => {
    if (user?.verification_status !== 'verified') {
      toast.error('Please complete identity verification first');
      navigate('/verification');
      return;
    }
    
    setBecomingHost(true);
    try {
      const response = await api.post('/users/become-host');
      
      // Update user context with fresh data from response
      if (response.data.user) {
        updateUser(response.data.user);
      }
      
      toast.success(response.data.message || 'You are now a Host!');
      
      // Force refresh user context to ensure UI updates
      await refreshUser();
      
      // Fetch host data now
      fetchData();
      fetchDashboardSummary();
    } catch (error) {
      // Check if user needs to accept Host Service Agreement first
      if (error.response?.data?.detail?.includes('Host Service Agreement')) {
        navigate('/host-agreement');
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to become host');
    } finally {
      setBecomingHost(false);
    }
  };

  // Get finance badge for a booking (from API or calculate locally)
  // DASHBOARD FINANCE BADGE - Uses unified host status system
  const getFinanceBadge = (booking) => {
    return getHostDashboardBadge(booking);
  };

  // CANONICAL STATUS BADGE - Uses unified host status system
  const getStatusBadge = (status) => {
    const config = getHostStatusConfig(status);
    const iconMap = {
      Clock, DollarSign, CheckCircle, Car, AlertCircle, AlertTriangle
    };
    const IconComponent = iconMap[config.icon] || Clock;
    
    return (
      <Badge className={`${config.color} gap-1 text-xs`}>
        <IconComponent className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  // Get counts from summary or calculate from local data
  const getVehiclesCount = () => dashboardSummary?.vehicles_listed_count ?? vehicles.length;
  const getPendingCount = () => dashboardSummary?.pending_requests_count ?? bookings.filter(b => normalizeHostStatus(b.status) === 'requested').length;
  const getActiveCount = () => dashboardSummary?.active_bookings_count ?? bookings.filter(b => 
    ['paid', 'prehire_in_progress', 'prehire_submitted', 'active', 'posthire_in_progress', 'posthire_submitted'].includes(b.status)
  ).length;
  
  // Get financials from summary or fallback to 0
  const getFinancials = () => dashboardSummary?.financials || { paid: 0, pending: 0, disputed: 0, lifetime: 0 };
  const getPayoutStatus = () => dashboardSummary?.payout_status || { configured: false };

  // Not a host - show become host prompt
  if (!isHost() && !loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Car className="w-8 h-8 text-[#004B8D]" />
              <span className="font-heading text-xl font-semibold text-slate-900">HireHub</span>
            </Link>
            <Button variant="ghost" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-12 text-center">
          <Car className="w-16 h-16 text-[#004B8D] mx-auto mb-6" />
          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-4">
            Become a Host
          </h1>
          <p className="text-slate-600 mb-6">
            List your vehicle and start earning. Join thousands of hosts on HireHub Australia.
          </p>
          
          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center gap-2 mb-8" data-testid="host-onboarding-trust-badges">
            <span className="inline-flex items-center gap-1.5 text-xs bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full border border-emerald-100">
              <ShieldCheck className="w-3.5 h-3.5" />
              Evidence-backed inspections
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full border border-blue-100">
              <FileText className="w-3.5 h-3.5" />
              Booking agreements stored
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs bg-purple-50 text-purple-700 px-3 py-1.5 rounded-full border border-purple-100">
              <CreditCard className="w-3.5 h-3.5" />
              Secure payment enforcement
            </span>
          </div>
          
          {user?.verification_status !== 'verified' ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <AlertTriangle className="w-5 h-5 text-amber-600 mx-auto mb-2" />
              <p className="text-sm text-amber-800">
                You need to verify your identity before becoming a host.
              </p>
              <Button 
                className="mt-4 bg-amber-600 hover:bg-amber-700"
                onClick={() => navigate('/verification')}
              >
                Start Verification
              </Button>
            </div>
          ) : (
            <Button 
              size="lg" 
              className="bg-[#004B8D] hover:bg-[#003a6d]"
              onClick={handleBecomeHost}
              disabled={becomingHost}
            >
              {becomingHost ? 'Processing...' : 'Become a Host'}
            </Button>
          )}
        </main>
      </div>
    );
  }

  return (
    <HostAppShell title="Host Dashboard" activeId="dashboard" showAddVehicle hideOnboardingBar>
      <HostOnboardingHelperModal />
      {loading || summaryLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#004B8D]"></div>
        </div>
      ) : (
        <div className="space-y-10">
          {/* Host Onboarding Checklist - Shows at top until complete */}
          <OnboardingChecklist />

          {/* Quick Start Guide reopen link (host_direct mode only) */}
          <div className="flex justify-end -mt-6">
            <HostOnboardingHelperLink />
          </div>

          {/* Host Tier Overview Card */}
          <HostTierCard tier={getSubscriptionTier()} hasFeature={hasFeature} />
          
          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* SECTION 1: OVERVIEW (MONEY) */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <section data-testid="section-overview">
            <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Wallet className="w-3.5 h-3.5" />
              Overview
            </h2>
            
            {/* Earnings Summary Row - Clickable cards that go to /host/payouts */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              <Link to="/host/payouts" data-testid="earnings-available-card">
                <Card className="border-green-200 bg-green-50/50 cursor-pointer hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-150 h-full">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xl font-bold text-green-700">${getFinancials().paid.toFixed(0)}</p>
                        <p className="text-xs text-green-600/80">Available</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>

              <Link to="/host/payouts" data-testid="earnings-pending-card">
                <Card className="border-blue-200 bg-blue-50/50 cursor-pointer hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-150 h-full">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xl font-bold text-blue-700">${getFinancials().pending.toFixed(0)}</p>
                        <p className="text-xs text-blue-600/80">Pending</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>

              <Link to="/host/payouts" data-testid="earnings-disputed-card">
                <Card className="border-amber-200 bg-amber-50/50 cursor-pointer hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-150 h-full">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                        <AlertOctagon className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-xl font-bold text-amber-700">${getFinancials().disputed.toFixed(0)}</p>
                        <p className="text-xs text-amber-600/80">Disputed</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>

              <Link to="/host/statements" data-testid="earnings-lifetime-card">
                <Card className="border-slate-200 bg-slate-50/50 cursor-pointer hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-150 h-full">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="text-xl font-bold text-slate-700">${getFinancials().lifetime.toFixed(0)}</p>
                        <p className="text-xs text-slate-500">Lifetime</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </div>

            {/* Next Payout Card - Clickable */}
            <Link to="/host/payouts" data-testid="next-payout-card" className="block mt-4">
              <Card className="border-slate-100 cursor-pointer hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.99] transition-all duration-150">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                        <Wallet className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium">Payout Settings</p>
                        <p className="text-sm text-slate-500">
                          {getPayoutStatus().configured ? (
                            <span className="text-green-600 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" /> Ready
                            </span>
                          ) : (
                            <span className="text-amber-600 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" /> Not configured
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm">
                        {getPayoutStatus().configured ? 'View' : 'Setup'}
                      </Button>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>
                  
                  {/* Next Payout Preview Line */}
                  {nextPayout && (
                    <div className="mt-3 pt-3 border-t border-slate-100 text-sm text-slate-600" data-testid="next-payout-preview">
                      <span>Next payout: </span>
                      <span className="font-medium text-slate-700">
                        {nextPayout.next_payout_at ? new Date(nextPayout.next_payout_at).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }) : 'N/A'}
                      </span>
                      <span> • Eligible: </span>
                      <span className="font-medium text-green-600">${(nextPayout.eligible_amount_cents / 100).toFixed(2)}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            </Link>

            {/* Simple Earnings CTA - Links to full Earnings & Payouts page */}
            <EarningsCTACard />
          </section>

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* SECTION 2: NEEDS ATTENTION (Only shown if there are actionable items) */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {(protectionStatus.issues?.length > 0 || !getPayoutStatus().configured) && (
            <section data-testid="section-needs-attention">
              <h2 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-4 flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5" />
                Needs Attention
              </h2>
              
              <div className="space-y-3">
                {/* Payout Not Configured Alert */}
                {!getPayoutStatus().configured && (
                  <Link to="/host/payouts">
                    <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 hover:shadow-md hover:-translate-y-0.5 active:scale-[0.99] transition-all duration-150 cursor-pointer">
                      <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <Wallet className="w-4 h-4 text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-amber-800">Payout settings not configured</p>
                        <p className="text-xs text-amber-600">Set up your payout method to receive earnings</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-amber-400" />
                    </div>
                  </Link>
                )}
                
                {/* Action Flags from Protection Status */}
                {protectionStatus.issues?.slice(0, 5).map((flag, idx) => (
                  <Link key={idx} to={flag.action_url || '/host/bookings'}>
                    <div className={`flex items-center gap-3 p-3 border rounded-lg hover:shadow-md hover:-translate-y-0.5 active:scale-[0.99] transition-all duration-150 cursor-pointer ${
                      flag.severity === 'critical' 
                        ? 'bg-red-50 border-red-200 hover:bg-red-100' 
                        : flag.severity === 'warning'
                        ? 'bg-amber-50 border-amber-200 hover:bg-amber-100'
                        : 'bg-blue-50 border-blue-200 hover:bg-blue-100'
                    }`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        flag.severity === 'critical' 
                          ? 'bg-red-100' 
                          : flag.severity === 'warning'
                          ? 'bg-amber-100'
                          : 'bg-blue-100'
                      }`}>
                        {flag.type?.includes('posthire') || flag.type?.includes('prehire') ? (
                          <ClipboardCheck className={`w-4 h-4 ${flag.severity === 'critical' ? 'text-red-600' : flag.severity === 'warning' ? 'text-amber-600' : 'text-blue-600'}`} />
                        ) : flag.type?.includes('insurance') ? (
                          <ShieldCheck className={`w-4 h-4 ${flag.severity === 'critical' ? 'text-red-600' : flag.severity === 'warning' ? 'text-amber-600' : 'text-blue-600'}`} />
                        ) : (
                          <AlertCircle className={`w-4 h-4 ${flag.severity === 'critical' ? 'text-red-600' : flag.severity === 'warning' ? 'text-amber-600' : 'text-blue-600'}`} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${flag.severity === 'critical' ? 'text-red-800' : flag.severity === 'warning' ? 'text-amber-800' : 'text-blue-800'}`}>
                          {flag.message}
                        </p>
                        {flag.urgency_label && (
                          <p className={`text-xs ${flag.severity === 'critical' ? 'text-red-600' : flag.severity === 'warning' ? 'text-amber-600' : 'text-blue-600'}`}>
                            {flag.urgency_label}
                          </p>
                        )}
                      </div>
                      <ChevronRight className={`w-4 h-4 ${flag.severity === 'critical' ? 'text-red-400' : flag.severity === 'warning' ? 'text-amber-400' : 'text-blue-400'}`} />
                    </div>
                  </Link>
                ))}
                
                {protectionStatus.issues?.length > 5 && (
                  <p className="text-xs text-center text-amber-600">
                    +{protectionStatus.issues.length - 5} more items need attention
                  </p>
                )}
              </div>
            </section>
          )}

          {/* GPS Tracker Alerts - Only shown if host has tracked vehicles */}
          <HostTrackerAlertsPanel />

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* SECTION 3: YOUR VEHICLES */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <section data-testid="section-vehicles">
            <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Car className="w-3.5 h-3.5" />
              Your Vehicles
            </h2>
            
            <Card className="border-slate-100 hover:shadow-md transition-shadow duration-150">
              <CardHeader className="pb-2">
                <Link to="/host/vehicles" className="flex items-center justify-between group">
                  <CardTitle className="font-heading text-lg group-hover:text-blue-600 transition-colors">My Vehicles</CardTitle>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 transition-colors" />
                </Link>
              </CardHeader>
              <CardContent>
                {vehicles.length === 0 ? (
                  <div className="text-center py-8">
                    <Car className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 mb-4">No vehicles listed yet</p>
                    <Link to="/host/vehicles/new">
                      <Button className="bg-[#004B8D] hover:bg-[#003a6d]">
                        <Plus className="w-4 h-4 mr-2" />
                        List Your First Vehicle
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {vehicles.slice(0, 6).map((vehicle) => (
                      <Link key={vehicle.vehicle_id} to={`/host/vehicles/${vehicle.vehicle_id}/edit`}>
                        <div className="group bg-slate-50 rounded-xl overflow-hidden hover:shadow-lg hover:-translate-y-1 active:scale-[0.98] transition-all duration-150 cursor-pointer">
                          <div className="aspect-video bg-slate-200 relative">
                            {filterValidPhotos(vehicle.photos)?.[0] ? (
                              <img 
                                src={getVehiclePhotoUrl(filterValidPhotos(vehicle.photos)[0])}
                                alt={`${vehicle.make} ${vehicle.model}`}
                                className="w-full h-full object-cover"
                                onError={(e) => handleVehicleImageError(e, vehicle.vehicle_id)}
                              />
                            ) : null}
                            <div className={`w-full h-full flex items-center justify-center absolute inset-0 ${filterValidPhotos(vehicle.photos)?.[0] ? 'hidden' : ''}`}>
                              <Car className="w-10 h-10 sm:w-12 sm:h-12 text-slate-400" />
                            </div>
                          </div>
                          <div className="p-3">
                            <p className="font-medium text-sm truncate">{vehicle.make} {vehicle.model}</p>
                            <p className="text-xs text-slate-500">${vehicle.daily_rate}/day</p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </section>

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* SECTION 4: RECENT ACTIVITY */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <section data-testid="section-activity">
            <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5" />
              Recent Activity
            </h2>
            
            {/* Quick Actions Row */}
            <div className="grid grid-cols-3 gap-4 mb-5">
              <Link to="/host/vehicles" data-testid="quick-action-vehicles">
                <Card className="border-slate-100 hover:shadow-lg hover:-translate-y-1 active:scale-[0.98] transition-all duration-150 cursor-pointer h-full">
                  <CardContent className="p-4 flex flex-col items-center text-center">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center mb-2">
                      <Car className="w-5 h-5 text-blue-600" />
                    </div>
                    <p className="text-sm font-medium">Manage Vehicles</p>
                    <p className="text-xs text-slate-400 mt-1">{getVehiclesCount()} listed</p>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/host/statements" data-testid="quick-action-statements">
                <Card className="border-slate-100 hover:shadow-lg hover:-translate-y-1 active:scale-[0.98] transition-all duration-150 cursor-pointer h-full">
                  <CardContent className="p-4 flex flex-col items-center text-center">
                    <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center mb-2">
                      <FileText className="w-5 h-5 text-green-600" />
                    </div>
                    <p className="text-sm font-medium">View Statements</p>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/host/bookings" data-testid="quick-action-bookings">
                <Card className="border-slate-100 hover:shadow-lg hover:-translate-y-1 active:scale-[0.98] transition-all duration-150 cursor-pointer h-full">
                  <CardContent className="p-4 flex flex-col items-center text-center">
                    <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center mb-2">
                      <Calendar className="w-5 h-5 text-amber-600" />
                    </div>
                    <p className="text-sm font-medium">View Bookings</p>
                    <p className="text-xs text-slate-400 mt-1">{getActiveCount()} active</p>
                  </CardContent>
                </Card>
              </Link>
            </div>

            {/* Recent Bookings - Clickable header */}
            <Card className="border-slate-100 hover:shadow-md transition-shadow duration-150">
              <CardHeader className="pb-2">
                <Link to="/host/bookings" className="flex items-center justify-between group">
                  <CardTitle className="font-heading text-lg group-hover:text-blue-600 transition-colors">Recent Bookings</CardTitle>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 transition-colors" />
                </Link>
              </CardHeader>
              <CardContent>
                {bookings.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No bookings yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {bookings.slice(0, 5).map((booking) => {
                      const badge = getFinanceBadge(booking);
                      return (
                        <Link key={booking.booking_id} to={`/bookings/${booking.booking_id}`}>
                          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 hover:shadow-md hover:-translate-y-0.5 active:scale-[0.99] transition-all duration-150 cursor-pointer">
                            <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center flex-shrink-0">
                              <Car className="w-5 h-5 text-slate-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">
                                {booking.vehicle?.make || 'Vehicle'} {booking.vehicle?.model || ''}
                              </p>
                              <p className="text-xs text-slate-500 truncate">
                                {booking.customer?.full_name || 'Customer'} • {booking.start_date}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <Badge className={`text-xs border ${badge.className}`}>
                                {badge.label}
                              </Badge>
                              {/* P1 FIX: Show compact countdown for awaiting payment */}
                              {booking.status === 'approved_pending_payment' && booking.payment_deadline_info && (
                                <PaymentCountdown 
                                  paymentDeadlineInfo={booking.payment_deadline_info}
                                  isHost={true}
                                  compact={true}
                                />
                              )}
                              {booking.subtotal && (
                                <span className="text-xs text-slate-500">${typeof booking.subtotal === 'number' ? booking.subtotal.toFixed(2) : booking.subtotal}</span>
                              )}
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </section>

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* SECTION 5: HIREHUB PROTECTION */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <section data-testid="section-protection">
            <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5" />
              Protection
            </h2>
            
            <Link to="/host/protection" data-testid="hirehub-protection-card">
              <Card className="border-[#004B8D]/20 bg-gradient-to-r from-[#004B8D]/5 to-blue-50/50 hover:shadow-xl hover:-translate-y-1 active:scale-[0.99] transition-all duration-150 cursor-pointer">
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#004B8D] to-[#0066CC] flex items-center justify-center flex-shrink-0 shadow-md">
                      <ShieldCheck className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
                        HireHub Protection
                        {/* Dynamic Protection Status Badge */}
                        {protectionStatus.status === 'protected' && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs border-0">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Fully Protected
                          </Badge>
                        )}
                        {protectionStatus.status === 'checklist_missing' && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs border-0">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {protectionStatus.checklistsMissing} Checklist{protectionStatus.checklistsMissing > 1 ? 's' : ''} Pending
                          </Badge>
                        )}
                        {protectionStatus.status === 'compliance_issue' && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs border-0">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Compliance Issue
                          </Badge>
                        )}
                        {protectionStatus.status === 'issues' && (
                          <Badge className="bg-red-100 text-red-700 text-xs border-0">
                            <AlertOctagon className="w-3 h-3 mr-1" />
                            Action Required
                          </Badge>
                        )}
                        {protectionStatus.status === 'loading' && (
                          <Badge className="bg-slate-100 text-slate-600 text-xs border-0">Active</Badge>
                        )}
                      </h3>
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                        {protectionStatus.status === 'protected' 
                          ? "Your protection is fully active. All inspections and compliance up to date."
                          : protectionStatus.status === 'checklist_missing'
                          ? "Complete pending checklists to maintain full claim eligibility."
                          : protectionStatus.status === 'compliance_issue'
                          ? "Review compliance items to ensure continuous protection."
                          : protectionStatus.status === 'issues'
                          ? "Multiple items need attention to maintain full protection."
                          : "Your vehicle is protected when you follow the required steps."
                        }
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0 hidden sm:block" />
                  </div>
                  {/* Trust Indicators */}
                  <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
                    <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-white/60 px-2 py-1 rounded-full">
                      <CheckCircle className="w-3 h-3 text-emerald-500" />
                      Photo evidence
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-white/60 px-2 py-1 rounded-full">
                      <CheckCircle className="w-3 h-3 text-emerald-500" />
                      Secure payments
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-white/60 px-2 py-1 rounded-full">
                      <CheckCircle className="w-3 h-3 text-emerald-500" />
                      Claim support
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </section>
          
          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* SECTION 6: REFERRALS (LOW PRIORITY) */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <HostInviteAndEarn api={api} />
        </div>
      )}
    </HostAppShell>
  );
};

export default HostDashboard;
